import React, { useMemo } from 'react';

import get from 'lodash/get';

import { SidePage, Section } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import PartnerCompanies from 'scenes/PartnerCompanies';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';
import { IoFormEdit, AmendmentFormCreate } from 'scenes/IO/components/IOForm';
import IOTitle from 'scenes/IO/components/IOTitle';
import {
  TAB_DETAILS,
  TAB_ACTIONS_LOG,
  TAB_ATTACHMENTS,
} from 'scenes/IO/constants';

import { useLoadIoDetails, useIsUpdating } from '../../data';
import Actions from '../Actions';
import IOLogs from './ActionsLog';
import Amendments from './Amendments';
import Attachments from './Attachments';
import BusinessPartnerContacts from './BusinessPartnerContacts';
import BusinessPartnerPaymentDetails from './BusinessPartnerPaymentDetails';
import General from './General';
import LegalContactPerson from './LegalContactPerson';
import Metadata from './Metadata';
import Remarks from './Remarks';
import Summary from './Summary';

const IODetails = ({
  id,
  activeTabId = TAB_DETAILS,
  ioStatusBeforeDocuSigning,
  onClose,
  onLoadIo = () => {},
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isRefetching } = useLoadIoDetails(
    { id, ioStatusBeforeDocuSigning },
    {
      onSuccess: onLoadIo,
    }
  );

  const businessPartnerId = get(data, 'businessPartner.id');

  const isAmendment = useMemo(() => data && data.parentIo, [data]);
  const isUpdating = useIsUpdating();

  const handleEdit = async () => {
    openChildComponent(IoFormEdit, {
      id: data.id,
    });
  };

  const handleDelete = async () => {
    onClose();
  };

  const handleAddAmendment = () => {
    openChildComponent(AmendmentFormCreate, {
      id: data.id,
    });
  };

  const handleOpenDetails = (_e, { id }) => {
    openChildComponent(IODetails, {
      id,
      ioStatusBeforeDocuSigning,
    });
  };

  const handleOpenBusinessPartner = (_e, { id }) => {
    openChildComponent(BusinessPartnerDetails, { id });
  };

  const handleOpenAffiliate = (_e, { id }) => {
    openChildComponent(AffiliateDetails, { id });
  };

  const handleOpenCustomerCompany = (_e, { id }) => {
    openChildComponent(PartnerCompanies, { id });
  };

  const amendmentCount = data?.amendments?.length;
  const isShowRemarks = Number(get(data, 'agreementType.id')) === 0;
  const isShowSummary = Number(get(data, 'agreementType.id')) === 0;

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isRefetching || isUpdating}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        entityId={`${data?.id}${
          businessPartnerId ? ` . BP # ${businessPartnerId}` : ''
        }`}
        title={`${isAmendment ? 'Amendment' : 'IO'}`}
        subTitle={data ? <IOTitle {...data} /> : null}
      />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            <SidePage.NewContent>
              <Section title={'General'}>
                <General
                  data={data}
                  onOpenBusinessPartner={handleOpenBusinessPartner}
                  onOpenDetails={handleOpenDetails}
                  onOpenAffiliate={handleOpenAffiliate}
                  onCustomerCompany={handleOpenCustomerCompany}
                />
              </Section>
              <Section
                title={'Business Partner Contacts'}
                isCollapsable
                initialOpen={false}
              >
                <BusinessPartnerContacts data={data} />
              </Section>
              <Section
                title={'Business Partner Payment Details'}
                isCollapsable
                initialOpen={false}
              >
                <BusinessPartnerPaymentDetails data={data} />
              </Section>
              <Section
                title={'Legal Contact Person'}
                isCollapsable
                initialOpen={false}
              >
                <LegalContactPerson data={data} />
              </Section>

              {amendmentCount ? (
                <Section
                  title={`Amendments (${amendmentCount})`}
                  isCollapsable
                  initialOpen={false}
                >
                  <Amendments
                    amendments={data.amendments}
                    onOpenDetails={handleOpenDetails}
                  />
                </Section>
              ) : null}

              {isShowRemarks ? (
                <Section title={'Remarks'} isCollapsable initialOpen={false}>
                  <Remarks isWeb={data?.isWeb} isMobile={data?.isMobile} />
                </Section>
              ) : null}

              {isShowSummary ? (
                <Section
                  title={'Summary of Certain Terms'}
                  isCollapsable
                  initialOpen={false}
                >
                  <Summary />
                </Section>
              ) : null}
            </SidePage.NewContent>
            <SidePage.Sidebar
              actions={
                <Actions
                  actions={data?.actions}
                  data={data}
                  onAddAmendment={handleAddAmendment}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  withViewLog={false}
                />
              }
            >
              <Metadata data={data} />
            </SidePage.Sidebar>
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ATTACHMENTS} count={data?.attachments?.length}>
          <SidePage.NewBody>
            <Attachments data={data?.attachments} />
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ACTIONS_LOG}>
          <SidePage.NewBody dense={true}>
            <IOLogs logs={data?.actionsLog} />
          </SidePage.NewBody>
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};
export default IODetails;
